import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

// Extend HTMLDivElement props and add our custom props
interface ScrollAreaProps extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactNode;
	// Custom scrollbar props - only used when customScrollbar is true
	customScrollbar?: boolean;
	scrollType?: 'auto' | 'always' | 'scroll' | 'hover';
	scrollHideDelay?: number;
	scrollOrientation?: 'vertical' | 'horizontal' | 'both';
	// Native scrollbar styling props - used when customScrollbar is false
	scrollbarStyle?: 'thin' | 'auto' | 'none';
	scrollbarColor?: string;
	// Pagination controls
	showPaginationButtons?: boolean;
	paginationButtonPosition?: 'inside' | 'outside';
	paginationScrollAmount?: number; // Percentage of viewport to scroll (0-1)
	onPaginationChange?: (
		direction: 'next' | 'previous',
		canScrollNext: boolean,
		canScrollPrevious: boolean,
	) => void;
	// Custom pagination render prop
	renderPagination?: (pagination: {
		canScrollNext: boolean;
		canScrollPrevious: boolean;
		scrollNext: () => void;
		scrollPrevious: () => void;
	}) => React.ReactNode;
}

// Performance optimized RAF throttling hook
function useThrottledCallback(callback: () => void, delay = 16) {
	const rafRef = useRef<number | undefined>(undefined);
	const lastCallRef = useRef(0);

	return useCallback(() => {
		const now = Date.now();
		if (now - lastCallRef.current >= delay) {
			lastCallRef.current = now;
			callback();
		} else {
			if (rafRef.current) {
				cancelAnimationFrame(rafRef.current);
			}
			rafRef.current = requestAnimationFrame(() => {
				lastCallRef.current = Date.now();
				callback();
			});
		}
	}, [callback, delay]);
}

// Pagination functionality hook
function usePagination(
	scrollElementRef: React.RefObject<HTMLDivElement>,
	orientation: 'vertical' | 'horizontal' | 'both',
	scrollAmount = 0.8,
	onPaginationChange?: (
		direction: 'next' | 'previous',
		canScrollNext: boolean,
		canScrollPrevious: boolean,
	) => void,
) {
	const [paginationState, setPaginationState] = useState({
		canScrollNext: false,
		canScrollPrevious: false,
	});

	const updatePaginationState = useCallback(() => {
		const element = scrollElementRef.current;
		if (!element) {
			return;
		}

		const isHorizontal = orientation === 'horizontal' || orientation === 'both';
		const isVertical = orientation === 'vertical' || orientation === 'both';

		let canScrollNext = false;
		let canScrollPrevious = false;

		if (isHorizontal) {
			const { scrollLeft, scrollWidth, clientWidth } = element;
			canScrollNext = scrollLeft < scrollWidth - clientWidth - 1;
			canScrollPrevious = scrollLeft > 1;
		} else if (isVertical) {
			const { scrollTop, scrollHeight, clientHeight } = element;
			canScrollNext = scrollTop < scrollHeight - clientHeight - 1;
			canScrollPrevious = scrollTop > 1;
		}

		setPaginationState((prev) => {
			if (prev.canScrollNext !== canScrollNext || prev.canScrollPrevious !== canScrollPrevious) {
				return { canScrollNext, canScrollPrevious };
			}
			return prev;
		});
	}, [scrollElementRef, orientation]);

	const scrollTo = useCallback(
		(direction: 'next' | 'previous') => {
			const element = scrollElementRef.current;
			if (!element) {
				return;
			}

			const isHorizontal = orientation === 'horizontal' || orientation === 'both';
			const isVertical = orientation === 'vertical' || orientation === 'both';

			if (isHorizontal) {
				const { scrollLeft, clientWidth } = element;
				const scrollDistance = clientWidth * scrollAmount;
				const newScrollLeft =
					direction === 'next' ? scrollLeft + scrollDistance : scrollLeft - scrollDistance;

				element.scrollTo({
					left: newScrollLeft,
					behavior: 'smooth',
				});
			} else if (isVertical) {
				const { scrollTop, clientHeight } = element;
				const scrollDistance = clientHeight * scrollAmount;
				const newScrollTop =
					direction === 'next' ? scrollTop + scrollDistance : scrollTop - scrollDistance;

				element.scrollTo({
					top: newScrollTop,
					behavior: 'smooth',
				});
			}

			// Update state after scroll animation
			setTimeout(() => {
				updatePaginationState();
				onPaginationChange?.(
					direction,
					paginationState.canScrollNext,
					paginationState.canScrollPrevious,
				);
			}, 300);
		},
		[
			scrollElementRef,
			orientation,
			scrollAmount,
			updatePaginationState,
			onPaginationChange,
			paginationState,
		],
	);

	return {
		...paginationState,
		scrollNext: () => scrollTo('next'),
		scrollPrevious: () => scrollTo('previous'),
		updatePaginationState,
	};
}

// Pagination button component
function PaginationButton({
	direction,
	onClick,
	disabled,
	position = 'inside',
	orientation = 'horizontal',
}: {
	direction: 'next' | 'previous';
	onClick: () => void;
	disabled: boolean;
	position?: 'inside' | 'outside';
	orientation?: 'vertical' | 'horizontal' | 'both';
}) {
	const isHorizontal = orientation === 'horizontal' || orientation === 'both';
	const isNext = direction === 'next';

	// Different positioning for horizontal vs vertical
	const getPositionClasses = () => {
		if (isHorizontal) {
			if (position === 'inside') {
				return isNext
					? 'absolute right-2 top-1/2 -translate-y-1/2 z-10'
					: 'absolute left-2 top-1/2 -translate-y-1/2 z-10';
			}
			return isNext
				? 'absolute -right-12 top-1/2 -translate-y-1/2'
				: 'absolute -left-12 top-1/2 -translate-y-1/2';
		}
		if (position === 'inside') {
			return isNext
				? 'absolute bottom-2 left-1/2 -translate-x-1/2 z-10'
				: 'absolute top-2 left-1/2 -translate-x-1/2 z-10';
		}
		return isNext
			? 'absolute -bottom-12 left-1/2 -translate-x-1/2'
			: 'absolute -top-12 left-1/2 -translate-x-1/2';
	};

	const getArrowIcon = () => {
		if (isHorizontal) {
			return isNext ? '→' : '←';
		}
		return isNext ? '↓' : '↑';
	};

	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			className={cn(
				getPositionClasses(),
				'flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white font-bold text-gray-600 text-sm shadow-md transition-all duration-200 hover:text-gray-900 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-md dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:text-white',
			)}
			aria-label={`Scroll ${direction}`}
		>
			{getArrowIcon()}
		</button>
	);
}

// Custom scrollbar implementation
function CustomScrollArea({
	ref,
	children,
	className = '',
	scrollType = 'hover',
	scrollHideDelay = 600,
	scrollOrientation = 'vertical',
	style,
	...divProps
}: ScrollAreaProps & { ref?: React.RefObject<HTMLDivElement | null> }) {
	const viewportRef = useRef<HTMLDivElement>(null);
	const verticalThumbRef = useRef<HTMLDivElement>(null);
	const horizontalThumbRef = useRef<HTMLDivElement>(null);
	const verticalTrackRef = useRef<HTMLDivElement>(null);
	const horizontalTrackRef = useRef<HTMLDivElement>(null);

	const [scrollState, setScrollState] = useState({
		isScrolling: false,
		showScrollbars: scrollType === 'always',
		vVisible: false,
		hVisible: false,
	});

	const hideTimeoutRef = useRef<number | undefined>(undefined);
	const isMouseOverRef = useRef(false);
	const isDraggingRef = useRef(false);
	const dragCleanupRef = useRef<(() => void) | null>(null);
	const lastScrollDataRef = useRef({
		vThumbHeight: 0,
		vThumbTop: 0,
		hThumbWidth: 0,
		hThumbLeft: 0,
	});

	const updateScrollbarsDOM = useCallback(() => {
		const viewport = viewportRef.current;
		const vThumb = verticalThumbRef.current;
		const hThumb = horizontalThumbRef.current;
		const vTrack = verticalTrackRef.current;
		const hTrack = horizontalTrackRef.current;

		if (!viewport) {
			return;
		}

		const { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth } =
			viewport;

		const hasVScroll =
			scrollHeight > clientHeight &&
			(scrollOrientation === 'vertical' || scrollOrientation === 'both');
		if (hasVScroll && vThumb && vTrack) {
			// Get actual track height
			const trackHeight = vTrack.clientHeight;
			// Calculate proportional thumb height
			const proportionalThumbHeight = (clientHeight / scrollHeight) * trackHeight;
			// Apply minimum size constraint
			const vThumbHeight = Math.max(20, proportionalThumbHeight);
			// Calculate available track space for thumb movement
			const availableTrackSpace = trackHeight - vThumbHeight;
			// Calculate scroll progress (0 to 1)
			const maxScrollTop = scrollHeight - clientHeight;
			const scrollProgress = maxScrollTop > 0 ? scrollTop / maxScrollTop : 0;
			// Position thumb within available track space
			const vThumbTop = scrollProgress * availableTrackSpace;

			const lastData = lastScrollDataRef.current;
			if (
				Math.abs(vThumbHeight - lastData.vThumbHeight) > 0.5 ||
				Math.abs(vThumbTop - lastData.vThumbTop) > 0.5
			) {
				vThumb.style.height = `${vThumbHeight}px`;
				vThumb.style.transform = `translateY(${vThumbTop}px)`;
				lastData.vThumbHeight = vThumbHeight;
				lastData.vThumbTop = vThumbTop;
			}
		}

		const hasHScroll =
			scrollWidth > clientWidth &&
			(scrollOrientation === 'horizontal' || scrollOrientation === 'both');
		if (hasHScroll && hThumb && hTrack) {
			// Get actual track width
			const trackWidth = hTrack.clientWidth;
			// Calculate proportional thumb width
			const proportionalThumbWidth = (clientWidth / scrollWidth) * trackWidth;
			// Apply minimum size constraint
			const hThumbWidth = Math.max(20, proportionalThumbWidth);
			// Calculate available track space for thumb movement
			const availableTrackSpace = trackWidth - hThumbWidth;
			// Calculate scroll progress (0 to 1)
			const maxScrollLeft = scrollWidth - clientWidth;
			const scrollProgress = maxScrollLeft > 0 ? scrollLeft / maxScrollLeft : 0;
			// Position thumb within available track space
			const hThumbLeft = scrollProgress * availableTrackSpace;

			const lastData = lastScrollDataRef.current;
			if (
				Math.abs(hThumbWidth - lastData.hThumbWidth) > 0.5 ||
				Math.abs(hThumbLeft - lastData.hThumbLeft) > 0.5
			) {
				hThumb.style.width = `${hThumbWidth}px`;
				hThumb.style.transform = `translateX(${hThumbLeft}px)`;
				lastData.hThumbWidth = hThumbWidth;
				lastData.hThumbLeft = hThumbLeft;
			}
		}

		if (hasVScroll !== scrollState.vVisible || hasHScroll !== scrollState.hVisible) {
			setScrollState((prev) => ({
				...prev,
				vVisible: hasVScroll,
				hVisible: hasHScroll,
			}));
		}
	}, [scrollOrientation, scrollState.vVisible, scrollState.hVisible]);

	const throttledUpdateScrollbars = useThrottledCallback(updateScrollbarsDOM, 16);

	const handleScroll = useCallback(() => {
		throttledUpdateScrollbars();

		if (scrollType === 'hover' || scrollType === 'scroll') {
			if (!scrollState.showScrollbars) {
				setScrollState((prev) => ({ ...prev, showScrollbars: true, isScrolling: true }));
			} else if (!scrollState.isScrolling) {
				setScrollState((prev) => ({ ...prev, isScrolling: true }));
			}

			if (hideTimeoutRef.current) {
				clearTimeout(hideTimeoutRef.current);
			}

			hideTimeoutRef.current = window.setTimeout(() => {
				setScrollState((prev) => ({
					...prev,
					isScrolling: false,
					showScrollbars: scrollType === 'hover' ? isMouseOverRef.current : false,
				}));
			}, scrollHideDelay);
		}
	}, [
		throttledUpdateScrollbars,
		scrollType,
		scrollHideDelay,
		scrollState.showScrollbars,
		scrollState.isScrolling,
	]);

	const handleMouseEnter = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			isMouseOverRef.current = true;
			if (scrollType === 'hover') {
				setScrollState((prev) => ({ ...prev, showScrollbars: true }));
			}
			divProps.onMouseEnter?.(e);
		},
		[scrollType, divProps],
	);

	const handleMouseLeave = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			isMouseOverRef.current = false;
			if (scrollType === 'hover' && !scrollState.isScrolling && !isDraggingRef.current) {
				setScrollState((prev) => ({ ...prev, showScrollbars: false }));
			}
			divProps.onMouseLeave?.(e);
		},
		[scrollType, scrollState.isScrolling, divProps],
	);

	const createThumbDragHandler = useCallback(
		(isVertical: boolean) => (e: React.MouseEvent) => {
			e.preventDefault();
			isDraggingRef.current = true;

			const viewport = viewportRef.current;
			if (!viewport) return;

			const { scrollHeight, clientHeight, scrollWidth, clientWidth } = viewport;
			const startPos = isVertical ? e.clientY : e.clientX;
			const startScroll = isVertical ? viewport.scrollTop : viewport.scrollLeft;

			const maxScroll = isVertical ? scrollHeight - clientHeight : scrollWidth - clientWidth;

			const handleMouseMove = (e: MouseEvent) => {
				const delta = (isVertical ? e.clientY : e.clientX) - startPos;
				// Fix: Use correct scroll ratio calculation
				const trackSize = isVertical ? clientHeight : clientWidth;
				const thumbSize = isVertical
					? lastScrollDataRef.current.vThumbHeight
					: lastScrollDataRef.current.hThumbWidth;
				const availableTrackSpace = trackSize - thumbSize;
				const scrollRatio = availableTrackSpace > 0 ? maxScroll / availableTrackSpace : 0;
				const newScroll = Math.max(0, Math.min(maxScroll, startScroll + delta * scrollRatio));

				if (isVertical) {
					viewport.scrollTop = newScroll;
				} else {
					viewport.scrollLeft = newScroll;
				}
			};

			const preventSelect = (e: Event) => e.preventDefault();

			const handleMouseUp = () => {
				isDraggingRef.current = false;
				document.removeEventListener('mousemove', handleMouseMove);
				document.removeEventListener('mouseup', handleMouseUp);
				document.removeEventListener('selectstart', preventSelect);
				dragCleanupRef.current = null;

				if (scrollType === 'hover' && !isMouseOverRef.current) {
					setScrollState((prev) => ({ ...prev, showScrollbars: false }));
				}
			};

			// Store cleanup function for potential component unmount
			dragCleanupRef.current = () => {
				isDraggingRef.current = false;
				document.removeEventListener('mousemove', handleMouseMove);
				document.removeEventListener('mouseup', handleMouseUp);
				document.removeEventListener('selectstart', preventSelect);
			};

			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
			document.addEventListener('selectstart', preventSelect);
		},
		[scrollType],
	);

	useEffect(() => {
		const viewport = viewportRef.current;
		if (!viewport) {
			return;
		}

		updateScrollbarsDOM();

		const resizeObserver = new ResizeObserver(updateScrollbarsDOM);
		resizeObserver.observe(viewport);

		return () => {
			resizeObserver.disconnect();
			if (hideTimeoutRef.current) {
				clearTimeout(hideTimeoutRef.current);
			}

			// Clean up any remaining drag event listeners
			if (dragCleanupRef.current) {
				dragCleanupRef.current();
			}
		};
	}, [updateScrollbarsDOM]);

	const containerClasses = cn('relative overflow-hidden', className);
	const shouldShowVScrollbar =
		scrollState.vVisible && (scrollState.showScrollbars || scrollType === 'always');
	const shouldShowHScrollbar =
		scrollState.hVisible && (scrollState.showScrollbars || scrollType === 'always');

	return (
		<div
			{...divProps}
			ref={ref}
			className={containerClasses}
			style={style}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<div
				ref={viewportRef}
				className="h-full w-full overflow-auto"
				onScroll={handleScroll}
				style={{
					scrollbarWidth: 'none',
					msOverflowStyle: 'none',
				}}
			>
				{children}
			</div>

			{/* Vertical Scrollbar Track */}
			{scrollState.vVisible && (
				<div
					ref={verticalTrackRef}
					className={cn(
						'pointer-events-none absolute top-0 right-0 h-full w-2.5 transition-opacity duration-150',
						shouldShowVScrollbar ? 'opacity-100' : 'opacity-0',
					)}
				>
					<div className="absolute top-0 right-0 h-full w-1 rounded-full bg-gray-200 dark:bg-gray-800" />
					<div
						ref={verticalThumbRef}
						className="pointer-events-auto absolute top-0 right-0 w-1 cursor-pointer rounded-full bg-gray-400 transition-colors duration-150 hover:bg-gray-500 dark:bg-gray-600 dark:hover:bg-gray-500"
						onMouseDown={createThumbDragHandler(true)}
						style={{ minHeight: '20px' }}
					/>
				</div>
			)}

			{/* Horizontal Scrollbar Track */}
			{scrollState.hVisible && (
				<div
					ref={horizontalTrackRef}
					className={cn(
						'pointer-events-none absolute bottom-0 left-0 h-2.5 w-full transition-opacity duration-150',
						shouldShowHScrollbar ? 'opacity-100' : 'opacity-0',
					)}
				>
					<div className="absolute bottom-0 left-0 h-1 w-full rounded-full bg-gray-200 dark:bg-gray-800" />
					<div
						ref={horizontalThumbRef}
						className="pointer-events-auto absolute bottom-0 left-0 h-1 cursor-pointer rounded-full bg-gray-400 transition-colors duration-150 hover:bg-gray-500 dark:bg-gray-600 dark:hover:bg-gray-500"
						onMouseDown={createThumbDragHandler(false)}
						style={{ minWidth: '20px' }}
					/>
				</div>
			)}

			<style>
				{`
        div::-webkit-scrollbar { display: none; }
      `}
			</style>
		</div>
	);
}
CustomScrollArea.displayName = 'CustomScrollArea';

// Native scrollbar component - tree-shakeable
function NativeScrollArea({
	ref,
	children,
	className = '',
	scrollbarStyle = 'thin',
	scrollbarColor,
	style,
	showPaginationButtons = false,
	paginationButtonPosition = 'inside',
	paginationScrollAmount = 0.8,
	scrollOrientation = 'vertical',
	onPaginationChange,
	renderPagination,
	...props
}: Omit<ScrollAreaProps, 'customScrollbar' | 'scrollType' | 'scrollHideDelay'> & {
	ref?: React.RefObject<HTMLDivElement | null>;
}) {
	const scrollElementRef = useRef<HTMLDivElement>(null);

	// Pagination functionality
	const pagination = usePagination(
		scrollElementRef as React.RefObject<HTMLDivElement>,
		scrollOrientation,
		paginationScrollAmount,
		onPaginationChange,
	);

	// Update pagination state on scroll
	const handleScroll = useCallback(() => {
		pagination.updatePaginationState();
	}, [pagination]);

	useEffect(() => {
		pagination.updatePaginationState();
	}, [pagination]);

	const scrollbarStyles: React.CSSProperties = {
		scrollbarWidth: scrollbarStyle,
		...(scrollbarColor && {
			scrollbarColor: `${scrollbarColor} transparent`,
		}),
		...style,
	};

	const nativeScrollClasses = cn(
		className,
		'overflow-auto',
		// Add some basic scrollbar styling classes that work with Tailwind
		scrollbarStyle === 'thin' && 'scrollbar-thin',
		scrollbarStyle === 'none' && 'scrollbar-none',
	);

	const containerClasses = cn('relative', paginationButtonPosition === 'outside' && 'mx-12');

	return (
		<div className={containerClasses}>
			<div
				{...props}
				ref={(node) => {
					scrollElementRef.current = node;
					if (ref && typeof ref !== 'function') {
						ref.current = node;
					}
				}}
				className={nativeScrollClasses}
				style={scrollbarStyles}
				onScroll={handleScroll}
			>
				{children}
				<style>
					{`
          /* Webkit scrollbar styling */
          div::-webkit-scrollbar {
            width: ${scrollbarStyle === 'thin' ? '8px' : scrollbarStyle === 'none' ? '0px' : '12px'};
            height: ${scrollbarStyle === 'thin' ? '8px' : scrollbarStyle === 'none' ? '0px' : '12px'};
          }
          
          div::-webkit-scrollbar-track {
            background: rgb(243 244 246);
            border-radius: 4px;
          }
          
          div::-webkit-scrollbar-thumb {
            background: ${scrollbarColor || 'rgb(156 163 175)'};
            border-radius: 4px;
            border: 1px solid rgb(243 244 246);
          }
          
          div::-webkit-scrollbar-thumb:hover {
            background: ${scrollbarColor ? `color-mix(in srgb, ${scrollbarColor} 80%, black)` : 'rgb(107 114 128)'};
          }
          
          @media (prefers-color-scheme: dark) {
            div::-webkit-scrollbar-track {
              background: rgb(31 41 55);
            }
            
            div::-webkit-scrollbar-thumb {
              background: ${scrollbarColor || 'rgb(75 85 99)'};
              border-color: rgb(31 41 55);
            }
            
            div::-webkit-scrollbar-thumb:hover {
              background: ${scrollbarColor ? `color-mix(in srgb, ${scrollbarColor} 80%, white)` : 'rgb(107 114 128)'};
            }
          }
        `}
				</style>
			</div>

			{/* Built-in Pagination Buttons */}
			{showPaginationButtons && !renderPagination && (
				<>
					<PaginationButton
						direction="previous"
						onClick={pagination.scrollPrevious}
						disabled={!pagination.canScrollPrevious}
						position={paginationButtonPosition}
						orientation={scrollOrientation}
					/>
					<PaginationButton
						direction="next"
						onClick={pagination.scrollNext}
						disabled={!pagination.canScrollNext}
						position={paginationButtonPosition}
						orientation={scrollOrientation}
					/>
				</>
			)}

			{/* Custom Pagination Render */}
			{renderPagination?.({
				canScrollNext: pagination.canScrollNext,
				canScrollPrevious: pagination.canScrollPrevious,
				scrollNext: pagination.scrollNext,
				scrollPrevious: pagination.scrollPrevious,
			})}
		</div>
	);
}
NativeScrollArea.displayName = 'NativeScrollArea';

// Main hybrid component - backwards compatibility
function ScrollArea({
	ref,
	children,
	className = '',
	customScrollbar = false,
	scrollbarStyle = 'thin',
	scrollbarColor,
	style,
	...props
}: ScrollAreaProps & { ref?: React.RefObject<HTMLDivElement | null> }) {
	// If custom scrollbar is not requested, use native scrolling
	if (!customScrollbar) {
		return (
			<NativeScrollArea
				{...props}
				ref={ref}
				className={className}
				scrollbarStyle={scrollbarStyle}
				scrollbarColor={scrollbarColor}
				style={style}
			>
				{children}
			</NativeScrollArea>
		);
	}

	// Use custom scrollbar implementation
	return (
		<CustomScrollArea {...props} ref={ref} className={className} style={style}>
			{children}
		</CustomScrollArea>
	);
}

ScrollArea.displayName = 'ScrollArea';

// Demo Component
function ScrollAreaDemo() {
	const longContent = Array.from({ length: 50 }, (_, i) => (
		<div key={i} className="border-gray-200 border-b p-2 dark:border-gray-700">
			Item {i + 1} - This content demonstrates the hybrid ScrollArea component that uses native
			scrolling by default.
		</div>
	));

	const wideContent = Array.from({ length: 20 }, (_, i) => (
		<div
			key={i}
			className="mx-2 flex h-16 w-32 items-center justify-center rounded border bg-blue-100 text-sm dark:bg-blue-900"
		>
			Card {i + 1}
		</div>
	));

	return (
		<div className="min-h-screen space-y-8 bg-white p-8 dark:bg-gray-900">
			<div>
				<h2 className="mb-4 font-bold text-2xl text-gray-900 dark:text-white">
					Hybrid ScrollArea Component
				</h2>
				<p className="mb-8 text-gray-600 dark:text-gray-400">
					Uses native scrolling by default, custom scrollbars only when explicitly requested.
				</p>

				<div className="grid gap-8 lg:grid-cols-2">
					{/* Native Scrolling Examples */}
					<div className="space-y-6">
						<h3 className="font-semibold text-gray-900 text-xl dark:text-white">
							Native Scrolling (Default)
						</h3>

						<div className="w-96">
							<h4 className="mb-2 font-medium text-gray-700 text-sm dark:text-gray-300">
								Default Native Scrollbar
							</h4>
							<ScrollArea
								className="h-48 w-full rounded-md border border-gray-300 p-4 dark:border-gray-600"
								id="native-default"
								data-testid="native-scroll"
							>
								{longContent.slice(0, 25)}
							</ScrollArea>
						</div>

						<div className="w-96">
							<h4 className="mb-2 font-medium text-gray-700 text-sm dark:text-gray-300">
								Thin Native Scrollbar
							</h4>
							<ScrollArea
								className="h-48 w-full rounded-md border border-gray-300 p-4 dark:border-gray-600"
								scrollbarStyle="thin"
							>
								{longContent.slice(0, 25)}
							</ScrollArea>
						</div>

						<div className="w-96">
							<h4 className="mb-2 font-medium text-gray-700 text-sm dark:text-gray-300">
								Custom Color Native
							</h4>
							<ScrollArea
								className="h-48 w-full rounded-md border border-gray-300 p-4 dark:border-gray-600"
								scrollbarStyle="thin"
								scrollbarColor="#3b82f6"
							>
								{longContent.slice(0, 25)}
							</ScrollArea>
						</div>

						<div className="w-96">
							<h4 className="mb-2 font-medium text-gray-700 text-sm dark:text-gray-300">
								Native Horizontal
							</h4>
							<ScrollArea
								className="w-full rounded-md border border-gray-300 p-4 dark:border-gray-600"
								scrollbarStyle="thin"
								scrollOrientation="horizontal"
							>
								<div className="flex w-max space-x-4">{wideContent}</div>
							</ScrollArea>
						</div>

						<div className="w-96">
							<h4 className="mb-2 font-medium text-gray-700 text-sm dark:text-gray-300">
								Native with Pagination (Inside)
							</h4>
							<NativeScrollArea
								className="w-full rounded-md border border-gray-300 p-4 dark:border-gray-600"
								scrollbarStyle="thin"
								scrollOrientation="horizontal"
								showPaginationButtons={true}
								paginationButtonPosition="inside"
								paginationScrollAmount={0.8}
							>
								<div className="flex w-max space-x-4">{wideContent}</div>
							</NativeScrollArea>
						</div>

						<div className="w-96">
							<h4 className="mb-2 font-medium text-gray-700 text-sm dark:text-gray-300">
								Native with Pagination (Outside)
							</h4>
							<NativeScrollArea
								className="w-full rounded-md border border-gray-300 p-4 dark:border-gray-600"
								scrollbarStyle="thin"
								scrollOrientation="horizontal"
								showPaginationButtons={true}
								paginationButtonPosition="outside"
								paginationScrollAmount={0.7}
								onPaginationChange={(direction, canNext, canPrev) => {
									// eslint-disable-next-line no-console -- Demo purposes only
									console.log(`Scrolled ${direction}, canNext: ${canNext}, canPrev: ${canPrev}`);
								}}
							>
								<div className="flex w-max space-x-4">{wideContent}</div>
							</NativeScrollArea>
						</div>

						<div className="w-96">
							<h4 className="mb-2 font-medium text-gray-700 text-sm dark:text-gray-300">
								Custom Pagination Render
							</h4>
							<div className="space-y-2">
								<NativeScrollArea
									className="w-full rounded-md border border-gray-300 p-4 dark:border-gray-600"
									scrollbarStyle="thin"
									scrollOrientation="horizontal"
									paginationScrollAmount={0.6}
									renderPagination={({
										canScrollNext,
										canScrollPrevious,
										scrollNext,
										scrollPrevious,
									}) => (
										<div className="mt-2 flex justify-center space-x-2">
											<button
												type="button"
												onClick={scrollPrevious}
												disabled={!canScrollPrevious}
												className="rounded bg-blue-500 px-3 py-1 text-white text-xs hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
											>
												← Previous
											</button>
											<button
												type="button"
												onClick={scrollNext}
												disabled={!canScrollNext}
												className="rounded bg-blue-500 px-3 py-1 text-white text-xs hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
											>
												Next →
											</button>
										</div>
									)}
								>
									<div className="flex w-max space-x-4">{wideContent}</div>
								</NativeScrollArea>
							</div>
						</div>
					</div>

					{/* Custom Scrolling Examples */}
					<div className="space-y-6">
						<h3 className="font-semibold text-gray-900 text-xl dark:text-white">
							Custom Scrollbars
						</h3>

						<div className="w-96">
							<h4 className="mb-2 font-medium text-gray-700 text-sm dark:text-gray-300">
								Custom Hover Scrollbar
							</h4>
							<ScrollArea
								customScrollbar={true}
								scrollType="hover"
								className="h-48 w-full rounded-md border border-gray-300 p-4 dark:border-gray-600"
							>
								{longContent.slice(0, 25)}
							</ScrollArea>
						</div>

						<div className="w-96">
							<h4 className="mb-2 font-medium text-gray-700 text-sm dark:text-gray-300">
								Always Visible Custom
							</h4>
							<ScrollArea
								customScrollbar={true}
								scrollType="always"
								className="h-48 w-full rounded-md border border-gray-300 p-4 dark:border-gray-600"
							>
								{longContent.slice(0, 25)}
							</ScrollArea>
						</div>

						<div className="w-96">
							<h4 className="mb-2 font-medium text-gray-700 text-sm dark:text-gray-300">
								Custom Horizontal
							</h4>
							<ScrollArea
								customScrollbar={true}
								scrollOrientation="horizontal"
								scrollType="hover"
								className="w-full rounded-md border border-gray-300 p-4 dark:border-gray-600"
							>
								<div className="flex w-max space-x-4">{wideContent}</div>
							</ScrollArea>
						</div>

						<div className="w-96">
							<h4 className="mb-2 font-medium text-gray-700 text-sm dark:text-gray-300">
								Both Directions Custom
							</h4>
							<ScrollArea
								customScrollbar={true}
								scrollOrientation="both"
								scrollType="hover"
								className="h-48 w-full rounded-md border border-gray-300 p-4 dark:border-gray-600"
							>
								<div className="w-[600px]">
									<div className="grid grid-cols-6 gap-2">
										{Array.from({ length: 48 }, (_, i) => (
											<div
												key={i}
												className="flex h-16 w-16 items-center justify-center rounded border bg-green-100 text-xs dark:bg-green-900"
											>
												{i + 1}
											</div>
										))}
									</div>
								</div>
							</ScrollArea>
						</div>
					</div>
				</div>

				<div className="mt-8">
					<h3 className="mb-4 font-semibold text-gray-900 text-lg dark:text-white">
						Standalone Hook Example
					</h3>
					<div className="w-96">
						<h4 className="mb-2 font-medium text-gray-700 text-sm dark:text-gray-300">
							Using useScrollPagination Hook Directly
						</h4>
						<StandalonePaginationExample />
					</div>
				</div>

				<div className="mt-8">
					<h3 className="mb-4 font-semibold text-gray-900 text-lg dark:text-white">
						Performance Comparison
					</h3>
					<div className="grid gap-4 md:grid-cols-2">
						<div className="w-96">
							<h4 className="mb-2 font-medium text-gray-700 text-sm dark:text-gray-300">
								Native (Recommended) - 1000 items
							</h4>
							<ScrollArea
								className="h-64 w-full rounded-md border border-gray-300 p-2 text-sm dark:border-gray-600"
								scrollbarStyle="thin"
							>
								{Array.from({ length: 1000 }, (_, i) => (
									<div key={i} className="border-gray-100 border-b p-1 dark:border-gray-800">
										Native Row {i + 1} - Zero JavaScript overhead, perfect accessibility
									</div>
								))}
							</ScrollArea>
						</div>

						<div className="w-96">
							<h4 className="mb-2 font-medium text-gray-700 text-sm dark:text-gray-300">
								Custom (Design-specific) - 1000 items
							</h4>
							<ScrollArea
								customScrollbar={true}
								scrollType="scroll"
								className="h-64 w-full rounded-md border border-gray-300 p-2 text-sm dark:border-gray-600"
							>
								{Array.from({ length: 1000 }, (_, i) => (
									<div key={i} className="border-gray-100 border-b p-1 dark:border-gray-800">
										Custom Row {i + 1} - Use only when design requires custom styling
									</div>
								))}
							</ScrollArea>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

// Standalone pagination hook for complete flexibility
function useScrollPagination(
	scrollElementRef: React.RefObject<HTMLDivElement>,
	options: {
		orientation?: 'vertical' | 'horizontal' | 'both';
		scrollAmount?: number;
		onPaginationChange?: (
			direction: 'next' | 'previous',
			canScrollNext: boolean,
			canScrollPrevious: boolean,
		) => void;
	} = {},
) {
	const { orientation = 'horizontal', scrollAmount = 0.8, onPaginationChange } = options;

	return usePagination(scrollElementRef, orientation, scrollAmount, onPaginationChange);
}

// Example component showing standalone hook usage
function StandalonePaginationExample() {
	const scrollRef = useRef<HTMLDivElement>(null);

	// Use the hook directly with any scroll container
	const pagination = useScrollPagination(scrollRef as React.RefObject<HTMLDivElement>, {
		orientation: 'horizontal',
		scrollAmount: 0.75,
		onPaginationChange: (direction, canNext, canPrev) => {
			// eslint-disable-next-line no-console -- Demo purposes only
			console.log(`Hook: Scrolled ${direction}, canNext: ${canNext}, canPrev: ${canPrev}`);
		},
	});

	// Handle scroll events to update pagination state
	const handleScroll = useCallback(() => {
		pagination.updatePaginationState();
	}, [pagination]);

	useEffect(() => {
		pagination.updatePaginationState();
	}, [pagination]);

	const items = Array.from({ length: 15 }, (_, i) => (
		<div
			key={i}
			className="mx-1 flex h-16 w-24 items-center justify-center rounded border bg-purple-100 text-xs dark:bg-purple-900"
		>
			Item {i + 1}
		</div>
	));

	return (
		<div className="space-y-3">
			{/* Custom controls positioned anywhere you want */}
			<div className="flex items-center justify-between">
				<button
					type="button"
					onClick={pagination.scrollPrevious}
					disabled={!pagination.canScrollPrevious}
					className="rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 px-4 py-2 text-white transition-all hover:from-purple-600 hover:to-pink-600 disabled:cursor-not-allowed disabled:opacity-50"
				>
					⏮ Previous Page
				</button>
				<span className="text-gray-600 text-sm dark:text-gray-400">
					{pagination.canScrollPrevious ? '← ' : ''}
					Custom Hook
					{pagination.canScrollNext ? ' →' : ''}
				</span>
				<button
					type="button"
					onClick={pagination.scrollNext}
					disabled={!pagination.canScrollNext}
					className="rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 px-4 py-2 text-white transition-all hover:from-purple-600 hover:to-pink-600 disabled:cursor-not-allowed disabled:opacity-50"
				>
					Next Page ⏭
				</button>
			</div>

			{/* Any regular div with overflow - no special scroll area component needed */}
			<div
				ref={scrollRef}
				className="scrollbar-thin flex overflow-x-auto rounded-md border border-gray-300 p-2 dark:border-gray-600"
				onScroll={handleScroll}
				style={{ scrollbarWidth: 'thin' }}
			>
				<div className="flex space-x-2">{items}</div>
			</div>

			{/* Progress indicator */}
			<div className="flex justify-center space-x-1">
				<div
					className={`h-2 w-2 rounded-full ${pagination.canScrollPrevious ? 'bg-gray-300' : 'bg-purple-500'}`}
				/>
				<div
					className={`h-2 w-2 rounded-full ${pagination.canScrollPrevious && pagination.canScrollNext ? 'bg-purple-500' : 'bg-gray-300'}`}
				/>
				<div
					className={`h-2 w-2 rounded-full ${pagination.canScrollNext ? 'bg-gray-300' : 'bg-purple-500'}`}
				/>
			</div>
		</div>
	);
}

export { CustomScrollArea, NativeScrollArea, ScrollArea, ScrollAreaDemo, useScrollPagination };
export default ScrollArea;
